import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import {
    IProductItemViewModel,
    IWishListTranslations,
} from 'Scripts/Types/Litium';

import { useAddToCartMutation } from 'Services/cartApi';

import CtaButton from 'Components/CtaButton';
import WishListButton from 'Components/WishListButton';

interface ProductCardInfoQuickBuyProps {
    priceVM?: IProductItemViewModel['priceVM'];
    name?: IProductItemViewModel['name'];
    sizes: IProductItemViewModel['productFilters']['sizes'];
    colors: IProductItemViewModel['productFilters']['colors'];
    values: IProductItemViewModel['productFilters']['values'];
    isOnWishList: IProductItemViewModel['isOnWishList'];
    enabledWishlist: IProductItemViewModel['enabledWishlist'];
    showQuantityField: IProductItemViewModel['showQuantityField'];
    additionalTranslations: IWishListTranslations;
    wishListIconText?: string;
}

const ProductCardInfoQuickBuy = ({
    sizes,
    priceVM,
    name,
    colors,
    values,
    isOnWishList,
    enabledWishlist,
    showQuantityField,
    additionalTranslations,
    wishListIconText,
}: ProductCardInfoQuickBuyProps) => {
    const isOneSizeAndIsEnabled = sizes.length === 1 && sizes[0].enabled;
    const [sizeOrValueList, setSizeOrValueList] = useState([
        ...values,
        ...sizes,
    ]);

    const [
        addToCart,
        {
            isLoading: isAddToCartLoading,
            isError: isErrorAddToCart,
            error: errorAddToCart,
        },
    ] = useAddToCartMutation();

    const [selectedVariant, setSelectedVariant] = useState(
        isOneSizeAndIsEnabled
    );

    const [itemQuantity, setItemQuantity] = useState(1);

    const handleChangeSizeVariant = (valueId: string) => {
        setSelectedVariant(true);
        setSizeOrValueList(
            sizeOrValueList.map(value => ({
                ...value,
                isActive: value.id === valueId,
            }))
        );
        setItemQuantity(1);
    };

    const sizeBtnWidth =
        (sizeOrValueList.length > 0 &&
            sizeOrValueList.reduce((longest, current) =>
                current && current.title.length > longest.title.length
                    ? current
                    : longest
            ).title.length) ||
        0;

    const ctaBtnDisabled =
        isAddToCartLoading ||
        !selectedVariant ||
        sizeOrValueList.findIndex(value => value.isActive && value.enabled) ===
            -1;

    const handleAddToCart = () => {
        if (showQuantityField) {
            addToCart({
                quantity: +itemQuantity,
                articleNumber: sizeOrValueList.find(
                    value => value.isActive && value.enabled
                ).id,
            });
        } else {
            addToCart({
                quantity: 1,
                articleNumber: sizeOrValueList.find(
                    value => value.isActive && value.enabled
                ).id,
            });
        }
    };

    useEffect(() => {
        if (isErrorAddToCart && 'error' in errorAddToCart) {
            const errMsg = errorAddToCart.error;
            toast.error(errMsg);
        }
    }, [isErrorAddToCart, errorAddToCart]);

    const campaignPrice = priceVM?.campaignPrice;
    const clubPrice = priceVM?.clubPrice;
    const basePrice = priceVM?.basePrice;
    const buyXPrice = priceVM?.buyXCampaigPrice;
    const buyXPricePerUnitText = priceVM?.buyXCampaignPricePerUnitText;

    const hasCampaignOrClubPrices =
        clubPrice?.formattedPrice.length > 0 ||
        campaignPrice?.formattedPrice.length > 0 ||
        buyXPrice?.formattedPrice.length > 0;

    return (
        <div className="relative flex h-full flex-col">
            <div className="flex w-full justify-between gap-1">
                <p className="mb-1 font-roboto text-sm font-light text-black lg:text-base">
                    {name}
                </p>
                <WishListButton
                    enabledWishlist={enabledWishlist}
                    isOnWishList={isOnWishList}
                    isOnWishListPage
                    id={sizeOrValueList.find(value => value.isActive).id}
                    name={name}
                    additionalTranslations={additionalTranslations}
                    colors={colors}
                    values={values}
                    stroke="1"
                    wishListIconText={wishListIconText}
                />
            </div>
            <div>
                <p className="mb-1 font-roboto text-base capitalize text-black">
                    {colors?.find(color => color.isActive)?.title}
                </p>
            </div>
            <ul className="price-container mb-2 flex flex-col flex-wrap">
                {clubPrice?.formattedPrice.length > 0 && (
                    <li
                        style={
                            {
                                '--hex-text-color':
                                    clubPrice?.priceListColorCode,
                            } as React.CSSProperties
                        }
                        className="font-roboto text-base font-medium leading-normal text-[var(--hex-text-color)] lg:text-lg"
                    >
                        <span>{clubPrice?.name} </span>
                        {clubPrice?.formattedPrice}
                    </li>
                )}
                {campaignPrice?.formattedPrice.length > 0 && (
                    <li
                        style={
                            {
                                '--hex-text-color':
                                    campaignPrice?.priceListColorCode,
                            } as React.CSSProperties
                        }
                        className="font-roboto text-base font-medium leading-normal text-[var(--hex-text-color)] lg:text-lg"
                    >
                        <span>{campaignPrice?.name} </span>
                        {campaignPrice?.formattedPrice}
                    </li>
                )}
                {buyXPrice?.formattedPrice.length > 0 && (
                    <li
                        style={
                            {
                                '--hex-text-color':
                                    buyXPrice?.priceListColorCode,
                            } as React.CSSProperties
                        }
                        className="font-roboto text-base font-medium leading-normal text-[var(--hex-text-color)] lg:text-lg"
                    >
                        <span>{buyXPrice?.name} </span>
                        <span>{buyXPrice?.formattedPrice} </span>
                        {buyXPricePerUnitText && buyXPricePerUnitText}
                    </li>
                )}
                {basePrice?.formattedPrice.length > 0 && (
                    <li
                        style={
                            {
                                '--hex-text-color':
                                    basePrice?.priceListColorCode,
                            } as React.CSSProperties
                        }
                        className={`font-roboto text-base font-normal  ${
                            !hasCampaignOrClubPrices
                                ? 'text-sm lg:text-base'
                                : 'text-base font-medium'
                        } leading-normal text-[var(--hex-text-color)]`}
                    >
                        <span>{basePrice?.name} </span>
                        {basePrice?.formattedPrice}
                    </li>
                )}
            </ul>
            <ul className="mb-3 flex flex-grow flex-wrap items-start gap-x-4 gap-y-2">
                {sizeOrValueList?.length > 0 &&
                    sizeOrValueList.map((size, index) => (
                        <li
                            key={`${size.id}-${index}`}
                            style={
                                {
                                    '--btn-size': `${sizeBtnWidth}ch`,
                                } as React.CSSProperties
                            }
                            className="flex w-[calc(var(--btn-size)_+_32px)] items-center"
                        >
                            <button
                                className={`size-selector flex w-full cursor-pointer border border-solid border-gray-300 font-roboto text-base font-semibold leading-normal text-black ${
                                    size.isActive && selectedVariant
                                        ? ' pointer-events-none bg-black text-white'
                                        : 'bg-white text-black'
                                }${
                                    size.enabled ? ' in-stock' : ' out-of-stock'
                                }`}
                                onClick={() => handleChangeSizeVariant(size.id)}
                            >
                                <span
                                    className={`mx-4 my-2 w-full font-roboto text-base font-bold leading-normal ${
                                        size.isActive && selectedVariant
                                            ? ' text-white'
                                            : 'text-black'
                                    }
                                                
                                                ${
                                                    size.enabled
                                                        ? 'text-black'
                                                        : 'line-through'
                                                } `}
                                >
                                    {size.title}
                                </span>
                            </button>
                        </li>
                    ))}
            </ul>
            {/* //!!TODO: add for quantity input field later  */}
            {/* <div className="buy-button flex w-full flex-wrap gap-2">
                {showQuantityField && (
                    <div className="flex flex-col gap-1">
                        <p>translations.quantity</p>
                        <QuantityInputField
                            value={itemQuantity}
                            minusAction={() =>
                                handleChangeQuantity(itemQuantity, true)
                            }
                            plusAction={() =>
                                handleChangeQuantity(itemQuantity, false)
                            }
                            minusDisabled={
                                itemQuantity === 1 || !selectedVariant
                            }
                            plusDisabled={!selectedVariant}
                            translations={{
                                decrementButton:
                                    'translations.checkoutDecrementButton',
                                incrementButton:
                                    'translations.checkoutIncreaseButton',
                                quantityLabel: 'translations.quantity',
                            }}
                        />
                    </div>
                )}
            </div> */}
            <div className="flex h-10">
                {sizeOrValueList.findIndex(value => value.isActive) >= 0 &&
                    selectedVariant && (
                        <p
                            style={
                                {
                                    '--dot-color': sizeOrValueList.find(
                                        value => value.isActive
                                    ).stockStateColor,
                                } as React.CSSProperties
                            }
                            className={` ${
                                selectedVariant ? 'flex' : ''
                            } items-center font-roboto text-sm before:mr-1.5 before:h-4.5 before:w-4.5 before:flex-[0_0_18px] before:rounded-full before:bg-[var(--dot-color)] before:content-['']`}
                        >
                            {
                                sizeOrValueList.find(value => value.isActive)
                                    .stockStateText
                            }
                        </p>
                    )}
            </div>
            <div className="flex">
                <CtaButton
                    className="flex h-[50px] w-full flex-auto items-center justify-center p-2.5 font-montserrat text-xl font-medium transition-colors duration-layout disabled:opacity-50 lg:text-base xl:text-xl"
                    onClick={handleAddToCart}
                    disabled={ctaBtnDisabled}
                    loading={isAddToCartLoading}
                >
                    {additionalTranslations.wishListPageBuyButtonLabel}
                </CtaButton>
            </div>
        </div>
    );
};

export default ProductCardInfoQuickBuy;
